import {BreakoutData, BreakoutModel} from 'models/program/BreakoutModel'
import {BreakoutroomModel} from 'models/program/BreakoutroomModel'
import {ProgramdayData, ProgramdayModel} from 'models/program/ProgramdayModel'
import {SequenceData, SequenceModel} from 'models/program/SequenceModel'
import {SequenceSessionModel} from 'models/program/SequenceSessionModel'
import {SessionData, SessionModel} from 'models/program/SessionModel'
import {SpeakerData, SpeakerModel} from 'models/program/SpeakerModel'
import {LinkType} from 'types'
import {Memoize} from 'util/memoize'

export type ProgramData = {
	url: string
	speakers: Array<SpeakerData>
	program:
		| null
		| {
				type: 'single'
				children: Array<SequenceData | BreakoutData | SessionData>
		  }
		| {type: 'multi'; days: Array<ProgramdayData>}
}

export type ProgramSchedule = Array<
	SequenceModel | BreakoutModel | SessionModel
>

export type ProgramNavigationItem =
	| SpeakerModel
	| SessionModel
	| SequenceSessionModel
	| BreakoutroomModel
	| SequenceModel

export class ProgramModel {
	data: ProgramData

	constructor(config: ProgramData) {
		this.data = config
	}

	@Memoize()
	get day(): ProgramdayModel {
		return null
	}

	@Memoize()
	get url(): string {
		return this.data.url
	}

	@Memoize()
	get program(): ProgramModel {
		return this
	}

	@Memoize()
	get speakerTypes(): Array<string> {
		const result: Set<string> = new Set()
		this.speakers.forEach(speaker => {
			speaker.types.forEach(type => result.add(type))
		})
		return Array.from(result)
	}

	@Memoize()
	get speakers(): Array<SpeakerModel> {
		return this.data.speakers.map(data => new SpeakerModel(this, data))
	}

	@Memoize()
	get hasMultipleDays(): boolean {
		return this.data.program?.type === 'multi'
	}

	@Memoize()
	get days(): Array<ProgramdayModel> {
		const program = this.data.program
		if (program.type !== 'multi') return []
		return program.days.map(data => new ProgramdayModel(this, data))
	}

	@Memoize()
	get sessions(): Array<SessionModel | SequenceSessionModel> {
		return this.navItems.filter(navItem => {
			if (navItem instanceof SessionModel) return true
			if (navItem instanceof SequenceSessionModel) return true
			return false
		}) as Array<SessionModel | SequenceSessionModel>
	}

	@Memoize()
	get schedule(): ProgramSchedule {
		const program = this.data.program
		switch (program?.type) {
			case null:
				return []
			case 'multi':
				return this.days.reduce(
					(result, day) => [...result, ...day.schedule],
					[] as ProgramSchedule
				)
			case 'single':
				return program.children.map(data => {
					switch (data.channel) {
						case 'breakout':
							return new BreakoutModel(this, data)
						case 'sequence':
							return new SequenceModel(this, data)
						case 'session':
							return new SessionModel(this, data)
					}
				})
		}
	}

	@Memoize()
	get navItems(): Array<ProgramNavigationItem> {
		if (!this.schedule) return []
		return this.schedule.reduce(
			(res: Array<ProgramNavigationItem>, obj) =>
				obj ? [...res, ...obj.navItems] : res,
			[]
		)
	}

	getProgramItemByDirectLink(url: string): ProgramNavigationItem {
		return this.getProgramItem(this.generatePathFromUrl(url))
	}

	getProgramItem(path: string): ProgramNavigationItem {
		const speaker = this.speakers.find(speaker => speaker.path === path)
		if (speaker) return speaker
		const item = this.navItems.find(session => session.path === path)
		if (item) return item
		return null
	}

	getProgramItemFromLink(link: LinkType): ProgramNavigationItem {
		if (!link) return null
		return this.getProgramItem(this.generatePathFromUrl(link.url))
	}

	generatePathFromUrl(url: string) {
		return url.slice(this.url.length)
	}
}
